import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Search, Edit, Trash2, Globe, FileSpreadsheet, Package, Settings, CreditCard, ArrowUpDown, ArrowUp, ArrowDown, Loader2, ChevronDown, AlertTriangle, X, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProductDialog } from "@/components/products/ProductDialog";
import { WebScrapeImporter } from "@/components/products/WebScrapeImporter";
import { CsvImportDialog } from "@/components/products/CsvImportDialog";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { PaywallOverlay } from "@/components/layout/PaywallOverlay";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const PRODUCTS_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

interface Product {
  id: string;
  name: string;
  type: "product" | "service" | "plan";
  description?: string;
  price?: number;
  sku?: string;
  stock?: number;
  colors?: string[] | string;
  dimensions?: string;
  measurements?: string[];
  is_active: boolean;
}

type SortField = "name" | "price" | "stock" | "type" | "status";
type SortDirection = "asc" | "desc";

function SortableHead({
  field,
  sortField,
  sortDirection,
  onSort,
  children,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  children: React.ReactNode;
}) {
  const isActive = sortField === field;
  const Icon = !isActive ? ArrowUpDown : sortDirection === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className="text-xs sm:text-sm p-0">
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center gap-1.5 px-4 h-12 w-full text-left font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        <span>{children}</span>
        <Icon className={`w-3 h-3 ${isActive ? "text-primary" : "opacity-50"}`} />
      </button>
    </TableHead>
  );
}

export default function Products() {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const { toast } = useToast();
  const { shouldShowPaywall } = useSubscription();
  const [products, setProducts] = useState<Product[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isScrapeDialogOpen, setIsScrapeDialogOpen] = useState(false);
  const [isCsvImportOpen, setIsCsvImportOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [selectAllAcrossSystem, setSelectAllAcrossSystem] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ current: number; total: number } | null>(null);
  const cancelDeleteRef = useRef(false);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; count: number; isAllSystem: boolean }>({ open: false, count: 0, isAllSystem: false });
  const [confirmTypedText, setConfirmTypedText] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [typeFilter, setTypeFilter] = useState<"all" | "product" | "plan" | "service">("all");
  const [planPricesMap, setPlanPricesMap] = useState<Record<string, { price: number; period: string; period_label: string | null }>>({});

  const getDisplayPrice = useCallback(
    (product: Product): { value: number; period?: string; periodLabel?: string | null } | null => {
      if (product.type === "plan") {
        const pp = planPricesMap[product.id];
        if (pp) return { value: pp.price, period: pp.period, periodLabel: pp.period_label };
      }
      if (typeof product.price === "number") return { value: product.price };
      return null;
    },
    [planPricesMap]
  );

  const formatPriceDisplay = useCallback(
    (product: Product): string => {
      const dp = getDisplayPrice(product);
      if (!dp) return "-";
      const base = `R$ ${dp.value.toFixed(2).replace(".", ",")}`;
      if (product.type !== "plan") return base;
      const periodMap: Record<string, string> = {
        monthly: "/mês",
        quarterly: "/trim",
        semiannual: "/sem",
        annual: "/ano",
        one_time: "",
        custom: "",
      };
      const suffix = dp.period ? periodMap[dp.period] ?? "" : "";
      return `${base}${suffix}`;
    },
    [getDisplayPrice]
  );

  const handleSortClick = useCallback((field: SortField) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDirection("asc");
      return;
    }
    if (sortDirection === "asc") {
      setSortDirection("desc");
      return;
    }
    // já estava desc → volta ao default
    setSortField("name");
    setSortDirection("asc");
  }, [sortField, sortDirection]);

  const fetchProductCount = useCallback(async () => {
    if (!workspaceId) return;
    try {
      let query = supabase
        .from("products")
        .select("id", { count: "exact", head: true })
        .eq("workspace_id", workspaceId);

      if (typeFilter !== "all") {
        query = query.eq("type", typeFilter);
      }

      const term = (debouncedSearchTerm || "").trim();
      if (term) {
        const escaped = term.replace(/%/g, "\\%").replace(/_/g, "\\_");
        query = query.or(`name.ilike.%${escaped}%,sku.ilike.%${escaped}%`);
      }

      const { count, error } = await query;
      if (error) {
        setTotalCount(null);
      } else {
        setTotalCount(count ?? 0);
      }
    } catch {
      setTotalCount(null);
    }
  }, [workspaceId, typeFilter, debouncedSearchTerm]);

  const getOrderConfig = useCallback(() => {
    const col = sortField === "status" ? "is_active" : sortField;
    const asc = sortField === "status" ? sortDirection === "asc" : sortDirection === "asc";
    return { column: col as "name" | "price" | "stock" | "type" | "is_active" | "created_at", ascending: asc };
  }, [sortField, sortDirection]);

  const fetchProducts = useCallback(
    async (pageNum: number, append: boolean) => {
      if (!workspaceId) return;
      try {
        if (append) setLoadingMore(true);
        else setLoading(true);

        const { column, ascending } = getOrderConfig();
        const from = append ? products.length : (pageNum - 1) * PRODUCTS_PAGE_SIZE;
        const toInclusive = from + PRODUCTS_PAGE_SIZE;

        let query = supabase
          .from("products")
          .select("*")
          .eq("workspace_id", workspaceId);

        if (typeFilter !== "all") {
          query = query.eq("type", typeFilter);
        }

        const term = (debouncedSearchTerm || "").trim();
        if (term) {
          const escaped = term.replace(/%/g, "\\%").replace(/_/g, "\\_");
          query = query.or(
            `name.ilike.%${escaped}%,sku.ilike.%${escaped}%`
          );
        }

        query = query
          .order(column, { ascending })
          .order("id", { ascending: true });

        const { data, error } = await query.range(from, toInclusive);

        if (error) {
          console.error("Error fetching products:", error);
          throw error;
        }

        const list = (data || []) as Product[];
        setHasMore(list.length > PRODUCTS_PAGE_SIZE);
        const slice = list.slice(0, PRODUCTS_PAGE_SIZE);

        if (append) {
          setProducts((prev) => [...prev, ...slice]);
        } else {
          setProducts(slice);
          setPage(pageNum);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Não foi possível carregar os produtos.";
        toast({
          title: "Erro ao carregar produtos",
          description: String(message),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [workspaceId, debouncedSearchTerm, getOrderConfig, products.length, typeFilter]
  );

  useEffect(() => {
    if (workspaceId) {
      fetchProductCount();
    }
  }, [workspaceId, fetchProductCount]);

  useEffect(() => {
    if (!workspaceId) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm, workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    setPage(1);
    fetchProducts(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only refetch when filters/sort change, not fetchProducts identity
  }, [workspaceId, debouncedSearchTerm, sortField, sortDirection, typeFilter]);

  // Fetch plan prices for visible plan products
  useEffect(() => {
    const planIds = products.filter((p) => p.type === "plan").map((p) => p.id);
    if (planIds.length === 0) {
      setPlanPricesMap({});
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("product_plan_prices")
        .select("product_id, price, period, period_label, is_highlighted, order_index")
        .in("product_id", planIds)
        .order("order_index", { ascending: true });
      if (cancelled || error || !data) return;
      const map: Record<string, { price: number; period: string; period_label: string | null }> = {};
      for (const row of data as Array<{ product_id: string; price: number | string; period: string; period_label: string | null; is_highlighted: boolean; order_index: number }>) {
        const priceNum = typeof row.price === "string" ? parseFloat(row.price) : row.price;
        if (!Number.isFinite(priceNum)) continue;
        const existing = map[row.product_id];
        if (!existing) {
          map[row.product_id] = { price: priceNum, period: row.period, period_label: row.period_label };
          continue;
        }
        // Prefer highlighted, then lowest order_index (already ordered), then min price
        if (row.is_highlighted) {
          map[row.product_id] = { price: priceNum, period: row.period, period_label: row.period_label };
        }
      }
      setPlanPricesMap(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [products]);

  useEffect(() => {
    if (!bulkSelectMode) return;
    const validIds = new Set(products.map((p) => p.id));
    setSelectedProductIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [products, bulkSelectMode]);

  const handleLoadMore = () => {
    fetchProducts(page, true);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchProducts(nextPage, false);
  };

  const totalPages = totalCount != null ? Math.max(1, Math.ceil(totalCount / PRODUCTS_PAGE_SIZE)) : 1;
  const filteredProducts = products;

  const handleToggleActive = async (productId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from("products")
        .update({ is_active: isActive })
        .eq("id", productId);

      if (error) {
        console.error("Error updating product:", error);
        throw error;
      }

      setProducts(
        products.map((p) => (p.id === productId ? { ...p, is_active: isActive } : p))
      );

      toast({
        title: "Produto atualizado",
        description: `Produto ${isActive ? "ativado" : "desativado"} com sucesso.`,
      });
    } catch (error: any) {
      const message = error.message?.includes("permission") || error.code === "42501"
        ? "Você não tem permissão para modificar produtos. Apenas administradores podem fazer alterações."
        : error.message || "Não foi possível atualizar o produto.";
      
      toast({
        title: "Erro ao atualizar produto",
        description: message,
        variant: "destructive",
      });
    }
  };

  const exitBulkSelectMode = () => {
    setBulkSelectMode(false);
    setSelectedProductIds([]);
    setSelectAllAcrossSystem(false);
  };

  const toggleSelectedProduct = (productId: string, checked: boolean) => {
    setSelectAllAcrossSystem(false);
    setSelectedProductIds((prev) => {
      if (checked) {
        if (prev.includes(productId)) return prev;
        return [...prev, productId];
      }
      return prev.filter((id) => id !== productId);
    });
  };

  const filteredSelectedCount = filteredProducts.reduce(
    (acc, p) => (selectedProductIds.includes(p.id) ? acc + 1 : acc),
    0,
  );
  const allFilteredSelected = filteredProducts.length > 0 && filteredSelectedCount === filteredProducts.length;
  const someFilteredSelected = filteredSelectedCount > 0 && !allFilteredSelected;
  const hasMoreInSystem = totalCount != null && totalCount > filteredProducts.length;
  const effectiveDeleteCount = selectAllAcrossSystem ? (totalCount ?? 0) : selectedProductIds.length;
  const hasActiveSearch = (debouncedSearchTerm || "").trim().length > 0;

  const handleSelectAllOnPage = () => {
    setSelectAllAcrossSystem(false);
    const ids = filteredProducts.map((p) => p.id);
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleClearSelection = () => {
    setSelectAllAcrossSystem(false);
    setSelectedProductIds([]);
  };

  const handleSelectAllAcrossSystem = () => {
    setSelectAllAcrossSystem(true);
    setSelectedProductIds(filteredProducts.map((p) => p.id));
  };

  const handleToggleSelectAllFiltered = (checkedState: boolean | "indeterminate") => {
    const checked = checkedState === true || checkedState === "indeterminate";
    if (checked) handleSelectAllOnPage();
    else handleClearSelection();
  };

  const openDeleteConfirm = () => {
    if (effectiveDeleteCount === 0) return;
    setConfirmTypedText("");
    setConfirmDialog({ open: true, count: effectiveDeleteCount, isAllSystem: selectAllAcrossSystem });
  };

  const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

  const deleteSelectedSimple = async () => {
    if (!workspaceId || selectedProductIds.length === 0) return;
    const count = selectedProductIds.length;
    try {
      // Defesa em profundidade: filtro explícito por workspace_id
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("workspace_id", workspaceId)
        .in("id", selectedProductIds);
      if (error) throw error;

      setPage(1);
      fetchProducts(1, false);
      fetchProductCount();
      exitBulkSelectMode();

      toast({
        title: "Itens excluídos",
        description: `${count} item(ns) removido(s) com sucesso.`,
      });
    } catch (error: any) {
      const message = error.message?.includes("permission") || error.code === "42501"
        ? "Você não tem permissão para excluir itens. Apenas administradores podem fazer alterações."
        : error.message || "Não foi possível excluir os itens.";

      toast({
        title: "Erro ao excluir",
        description: message,
        variant: "destructive",
      });
    }
  };

  const deleteAllAcrossSystem = async () => {
    if (!workspaceId) return;
    cancelDeleteRef.current = false;

    let deletedCount = 0;
    let failureError: string | null = null;

    try {
      // 1) Coletar IDs do workspace (paginado), respeitando filtro de busca
      const allIds: string[] = [];
      const FETCH_PAGE = 1000;
      let offset = 0;
      const term = (debouncedSearchTerm || "").trim();
      setBulkDeleteProgress({ current: 0, total: totalCount ?? 0 });

      while (!cancelDeleteRef.current) {
        let q = supabase
          .from("products")
          .select("id")
          .eq("workspace_id", workspaceId);
        if (typeFilter !== "all") {
          q = q.eq("type", typeFilter);
        }
        if (term) {
          const escaped = term.replace(/%/g, "\\%").replace(/_/g, "\\_");
          q = q.or(`name.ilike.%${escaped}%,sku.ilike.%${escaped}%`);
        }
        const { data, error } = await q.range(offset, offset + FETCH_PAGE - 1);
        if (error) throw error;
        const batch = (data || []) as { id: string }[];
        if (batch.length === 0) break;
        allIds.push(...batch.map((r) => r.id));
        if (batch.length < FETCH_PAGE) break;
        offset += FETCH_PAGE;
      }

      if (cancelDeleteRef.current) {
        toast({ title: "Operação cancelada", description: "Nenhum produto foi excluído." });
        return;
      }

      const total = allIds.length;
      setBulkDeleteProgress({ current: 0, total });

      if (total === 0) {
        toast({ title: "Nada para excluir", description: "Nenhum produto encontrado." });
        return;
      }

      // 2) Loop de delete em chunks de 300
      const CHUNK = 300;
      for (let i = 0; i < total; i += CHUNK) {
        if (cancelDeleteRef.current) break;
        const chunk = allIds.slice(i, i + CHUNK);
        if (chunk.length === 0) continue;

        const { error } = await supabase
          .from("products")
          .delete()
          .eq("workspace_id", workspaceId)
          .in("id", chunk);

        if (error) {
          failureError = error.message?.includes("permission") || (error as any).code === "42501"
            ? "Sem permissão para excluir produtos."
            : error.message || "Erro desconhecido ao excluir lote.";
          break;
        }

        deletedCount += chunk.length;
        setBulkDeleteProgress({ current: deletedCount, total });
        await sleep(50);
      }

      if (failureError) {
        toast({
          title: "Exclusão interrompida",
          description: `${deletedCount} produto(s) excluído(s) antes do erro: ${failureError}`,
          variant: "destructive",
        });
      } else if (cancelDeleteRef.current) {
        toast({
          title: "Exclusão interrompida",
          description: `${deletedCount} de ${total} produto(s) foram excluídos.`,
        });
      } else {
        toast({
          title: "Produtos excluídos",
          description: `${deletedCount} produto(s) removido(s) com sucesso.`,
        });
      }
    } catch (error: any) {
      const message = error.message?.includes("permission") || error.code === "42501"
        ? "Você não tem permissão para excluir produtos."
        : error.message || "Falha ao excluir produtos.";
      toast({
        title: "Erro ao excluir",
        description: `${deletedCount} excluído(s) antes do erro. ${message}`,
        variant: "destructive",
      });
    } finally {
      setBulkDeleteProgress(null);
      cancelDeleteRef.current = false;
      setPage(1);
      fetchProducts(1, false);
      fetchProductCount();
      exitBulkSelectMode();
    }
  };

  const handleConfirmDelete = async () => {
    const isAll = confirmDialog.isAllSystem;
    const count = confirmDialog.count;
    if (count > 100 && confirmTypedText !== "APAGAR") return;
    setConfirmDialog({ open: false, count: 0, isAllSystem: false });
    setConfirmTypedText("");
    if (isAll) {
      await deleteAllAcrossSystem();
    } else {
      await deleteSelectedSimple();
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    if (!confirm(`Tem certeza que deseja excluir "${product.name}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", product.id);

      if (error) throw error;

      setPage(1);
      fetchProducts(1, false);
      fetchProductCount();

      toast({
        title: "Item excluído",
        description: `${product.type === "product" ? "Produto" : "Serviço"} removido com sucesso.`,
      });
    } catch (error: any) {
      const message = error.message?.includes("permission") || error.code === "42501"
        ? "Você não tem permissão para excluir itens. Apenas administradores podem fazer alterações."
        : error.message || "Não foi possível excluir o item.";
      
      toast({
        title: "Erro ao excluir",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleCreateProduct = () => {
    setSelectedProduct(null);
    setIsDialogOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (shouldRefresh: boolean) => {
    setIsDialogOpen(false);
    setSelectedProduct(null);
    if (shouldRefresh) {
      setPage(1);
      fetchProducts(1, false);
      fetchProductCount();
    }
  };

  const refreshProductsAndStats = useCallback(() => {
    setPage(1);
    fetchProducts(1, false);
    fetchProductCount();
  }, [fetchProducts, fetchProductCount]);

  return (
    <div className="p-2 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 lg:space-y-6 relative">
      {shouldShowPaywall && <PaywallOverlay featureName="Produtos e Serviços" minPlan="Inicial" />}
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">Produtos, Serviços e Planos</h1>
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <Button variant="outline" onClick={() => setIsCsvImportOpen(true)} size="sm" className="flex-1 sm:flex-none h-9 sm:h-10">
            <FileSpreadsheet className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
            <span className="text-xs sm:text-sm">Importar CSV</span>
          </Button>
          <Button variant="outline" onClick={() => setIsScrapeDialogOpen(true)} size="sm" className="flex-1 sm:flex-none h-9 sm:h-10">
            <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
            <span className="text-xs sm:text-sm">Importar de URL</span>
          </Button>
          <Button onClick={handleCreateProduct} size="sm" className="flex-1 sm:flex-none h-9 sm:h-10">
            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
            <span className="text-xs sm:text-sm">Novo Item</span>
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <Input
            placeholder="Buscar por nome ou SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 sm:pl-10 h-9 sm:h-10 text-sm"
          />
        </div>
        <Select
          value={`${sortField}-${sortDirection}`}
          onValueChange={(val) => {
            const [field, dir] = val.split("-") as [SortField, SortDirection];
            setSortField(field);
            setSortDirection(dir);
          }}
        >
          <SelectTrigger className="w-[180px] h-9 sm:h-10 text-sm">
            <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue placeholder="Ordenar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name-asc">Nome (A-Z)</SelectItem>
            <SelectItem value="name-desc">Nome (Z-A)</SelectItem>
            <SelectItem value="price-asc">Preço (menor)</SelectItem>
            <SelectItem value="price-desc">Preço (maior)</SelectItem>
            <SelectItem value="stock-asc">Estoque (menor)</SelectItem>
            <SelectItem value="stock-desc">Estoque (maior)</SelectItem>
            <SelectItem value="type-asc">Tipo (A-Z)</SelectItem>
            <SelectItem value="status-desc">Ativos primeiro</SelectItem>
            <SelectItem value="status-asc">Inativos primeiro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="px-3 sm:px-6 pt-3 sm:pt-6 pb-2 sm:pb-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base sm:text-lg lg:text-xl flex items-center gap-2 flex-wrap">
              <span>Catálogo</span>
              {totalCount != null && (
                <span className="text-muted-foreground font-normal text-sm sm:text-base">
                  ({totalCount}{" "}
                  {typeFilter === "plan"
                    ? totalCount === 1 ? "plano cadastrado" : "planos cadastrados"
                    : typeFilter === "service"
                      ? totalCount === 1 ? "serviço cadastrado" : "serviços cadastrados"
                      : typeFilter === "product"
                        ? totalCount === 1 ? "produto cadastrado" : "produtos cadastrados"
                        : totalCount === 1 ? "item cadastrado" : "itens cadastrados"})
                </span>
              )}
              {typeFilter !== "all" && (
                <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-secondary/80" onClick={() => setTypeFilter("all")}>
                  <Filter className="w-3 h-3" />
                  {typeFilter === "plan" ? "Planos" : typeFilter === "service" ? "Serviços" : "Produtos"}
                  <X className="w-3 h-3" />
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {bulkSelectMode ? (
                <>
                  {hasMoreInSystem ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-9" disabled={filteredProducts.length === 0}>
                          {selectAllAcrossSystem || filteredSelectedCount > 0 ? "Alterar seleção" : "Selecionar todos"}
                          <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64 bg-popover z-50">
                        <DropdownMenuItem onClick={handleSelectAllOnPage}>
                          Selecionar todos desta página ({filteredProducts.length})
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleSelectAllAcrossSystem}>
                          {hasActiveSearch
                            ? `Selecionar todos os resultados da busca (${totalCount ?? 0})`
                            : `Selecionar todos do sistema (${totalCount ?? 0})`}
                        </DropdownMenuItem>
                        {(selectAllAcrossSystem || selectedProductIds.length > 0) && (
                          <DropdownMenuItem onClick={handleClearSelection}>
                            Limpar seleção
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9"
                      onClick={() => handleToggleSelectAllFiltered(allFilteredSelected ? false : true)}
                      disabled={filteredProducts.length === 0}
                    >
                      {allFilteredSelected ? "Limpar seleção" : "Selecionar todos"}
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-9"
                    disabled={effectiveDeleteCount === 0}
                    onClick={openDeleteConfirm}
                  >
                    {selectAllAcrossSystem
                      ? `Apagar todos (${effectiveDeleteCount})`
                      : `Apagar${effectiveDeleteCount > 0 ? ` (${effectiveDeleteCount})` : ""}`}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-9" onClick={exitBulkSelectMode}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={() => setBulkSelectMode(true)}
                >
                  Selecionar para apagar
                </Button>
              )}
            </div>
          </div>
          {bulkSelectMode && selectAllAcrossSystem && (
            <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-200">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>
                  {hasActiveSearch
                    ? `Todos os ${totalCount ?? 0} resultados da busca serão apagados.`
                    : `Todos os ${totalCount ?? 0} produtos cadastrados serão apagados.`}
                </span>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-amber-900 dark:text-amber-200" onClick={handleClearSelection}>
                Limpar seleção
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="px-0 sm:px-6 pb-3 sm:pb-6">
          {loading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Carregando produtos...
            </div>
          )}
          {!loading && (
          <>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                  {bulkSelectMode && (
                    <TableHead className="w-10">
                      <div className="flex items-center justify-center">
                        <Checkbox
                          checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
                          onCheckedChange={handleToggleSelectAllFiltered}
                          aria-label="Selecionar todos"
                        />
                      </div>
                    </TableHead>
                  )}
                  <TableHead className="text-xs sm:text-sm p-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-1.5 px-4 h-12 w-full text-left font-medium text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <span>Tipo</span>
                          {typeFilter !== "all" ? (
                            <Filter className="w-3 h-3 text-primary" />
                          ) : (
                            <ChevronDown className="w-3 h-3 opacity-50" />
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="bg-popover z-50">
                        <DropdownMenuItem onClick={() => setTypeFilter("all")}>
                          {typeFilter === "all" && <span className="mr-2">✓</span>}
                          Todos
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setTypeFilter("product")}>
                          {typeFilter === "product" && <span className="mr-2">✓</span>}
                          <Package className="w-3.5 h-3.5 mr-1.5" />
                          Produtos
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setTypeFilter("plan")}>
                          {typeFilter === "plan" && <span className="mr-2">✓</span>}
                          <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                          Planos
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setTypeFilter("service")}>
                          {typeFilter === "service" && <span className="mr-2">✓</span>}
                          <Settings className="w-3.5 h-3.5 mr-1.5" />
                          Serviços
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableHead>
                  <SortableHead field="name" sortField={sortField} sortDirection={sortDirection} onSort={handleSortClick}>Nome</SortableHead>
                  <TableHead className="text-xs sm:text-sm">SKU</TableHead>
                  <SortableHead field="price" sortField={sortField} sortDirection={sortDirection} onSort={handleSortClick}>Preço</SortableHead>
                  <TableHead className="text-xs sm:text-sm">Cores</TableHead>
                  <TableHead className="text-xs sm:text-sm">Medidas</TableHead>
                  <SortableHead field="stock" sortField={sortField} sortDirection={sortDirection} onSort={handleSortClick}>Estoque</SortableHead>
                  <SortableHead field="status" sortField={sortField} sortDirection={sortDirection} onSort={handleSortClick}>Status</SortableHead>
                  <TableHead className="text-right text-xs sm:text-sm">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id}>
                    {bulkSelectMode && (
                      <TableCell className="align-middle">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={selectedProductIds.includes(product.id)}
                            onCheckedChange={(checked) => toggleSelectedProduct(product.id, checked === true)}
                            aria-label={`Selecionar ${product.name}`}
                          />
                        </div>
                      </TableCell>
                    )}
                    <TableCell className="text-xs sm:text-sm">
                      <Badge variant="outline" className="text-[10px] sm:text-xs flex items-center gap-1.5 w-fit">
                        {product.type === "product" ? (
                          <>
                            <Package className="w-3 h-3 flex-shrink-0" />
                            <span>Produto</span>
                          </>
                        ) : product.type === "plan" ? (
                          <>
                            <CreditCard className="w-3 h-3 flex-shrink-0" />
                            <span>Plano</span>
                          </>
                        ) : (
                          <>
                            <Settings className="w-3 h-3 flex-shrink-0" />
                            <span>Serviço</span>
                          </>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium text-xs sm:text-sm">{product.name}</TableCell>
                    <TableCell className="text-xs sm:text-sm">{product.sku || "-"}</TableCell>
                    <TableCell className="text-xs sm:text-sm">
                    {formatPriceDisplay(product)}
                  </TableCell>
                    <TableCell className="text-xs sm:text-sm">
                    {Array.isArray(product.colors) && product.colors.length > 0 
                      ? product.colors.join(", ") 
                      : (product.colors || "-")}
                  </TableCell>
                    <TableCell className="text-xs sm:text-sm">
                      {Array.isArray(product.measurements) && product.measurements.length > 0
                        ? product.measurements.join(", ")
                        : product.dimensions || "-"}
                    </TableCell>
                    <TableCell className="text-xs sm:text-sm">{product.stock || 0}</TableCell>
                    <TableCell className="text-xs sm:text-sm">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={product.is_active}
                        onCheckedChange={(checked) =>
                          handleToggleActive(product.id, checked)
                        }
                      />
                        <Badge variant={product.is_active ? "default" : "secondary"} className="text-[10px] sm:text-xs">
                        {product.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!bulkSelectMode && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditProduct(product)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteProduct(product)}
                            className="text-destructive hover:text-destructive h-8 w-8 p-0"
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredProducts.length === 0 && (
                <TableRow>
                    <TableCell
                      colSpan={bulkSelectMode ? 10 : 9}
                      className="text-center text-muted-foreground text-xs sm:text-sm py-8"
                    >
                    Nenhum item encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3 px-3 sm:px-4">
            {filteredProducts.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 text-sm">
                Nenhum item encontrado
              </div>
            ) : (
              filteredProducts.map((product) => (
                <Card key={product.id} className="border">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {bulkSelectMode && (
                          <Checkbox
                            checked={selectedProductIds.includes(product.id)}
                            onCheckedChange={(checked) => toggleSelectedProduct(product.id, checked === true)}
                            aria-label={`Selecionar ${product.name}`}
                            className="mt-1"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-[10px] flex items-center gap-1.5 w-fit">
                            {product.type === "product" ? (
                              <>
                                <Package className="w-3 h-3 flex-shrink-0" />
                                <span>Produto</span>
                              </>
                            ) : product.type === "plan" ? (
                              <>
                                <CreditCard className="w-3 h-3 flex-shrink-0" />
                                <span>Plano</span>
                              </>
                            ) : (
                              <>
                                <Settings className="w-3 h-3 flex-shrink-0" />
                                <span>Serviço</span>
                              </>
                            )}
                          </Badge>
                          <Badge variant={product.is_active ? "default" : "secondary"} className="text-[10px]">
                            {product.is_active ? "Ativo" : "Inativo"}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-sm truncate">{product.name}</h3>
                        {product.sku && (
                          <p className="text-xs text-muted-foreground">SKU: {product.sku}</p>
                        )}
                      </div>
                      </div>
                      {!bulkSelectMode && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditProduct(product)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteProduct(product)}
                            className="text-destructive hover:text-destructive h-8 w-8 p-0"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {getDisplayPrice(product) && (
                        <div>
                          <p className="text-muted-foreground mb-0.5">Preço</p>
                          <p className="font-medium">{formatPriceDisplay(product)}</p>
                        </div>
                      )}
                      {product.stock !== undefined && (
                        <div>
                          <p className="text-muted-foreground mb-0.5">Estoque</p>
                          <p className="font-medium">{product.stock || 0}</p>
                        </div>
                      )}
                      {product.colors && (
                        <div className="col-span-2">
                          <p className="text-muted-foreground mb-0.5">Cores</p>
                          <p className="font-medium">
                            {Array.isArray(product.colors) && product.colors.length > 0 
                              ? product.colors.join(", ") 
                              : (product.colors || "-")}
                          </p>
                        </div>
                      )}
                      {(product.measurements?.length || product.dimensions) && (
                        <div className="col-span-2">
                          <p className="text-muted-foreground mb-0.5">Medidas</p>
                          <p className="font-medium">
                            {Array.isArray(product.measurements) && product.measurements.length > 0
                              ? product.measurements.join(", ")
                              : product.dimensions || "-"}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={product.is_active}
                          onCheckedChange={(checked) =>
                            handleToggleActive(product.id, checked)
                          }
                        />
                        <span className="text-xs text-muted-foreground">
                          {product.is_active ? "Ativo" : "Inativo"}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Paginação e Carregar mais */}
          {(products.length > 0 || loading) && (
            <div className="mt-4 pt-4 border-t px-3 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
              {!debouncedSearchTerm.trim() && totalCount != null && totalCount > PRODUCTS_PAGE_SIZE && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page > 1) handlePageChange(page - 1);
                        }}
                        className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                        aria-disabled={page <= 1}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <span className="text-sm text-muted-foreground px-2">
                        Página {page} de {totalPages}
                      </span>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page < totalPages) handlePageChange(page + 1);
                        }}
                        className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                        aria-disabled={page >= totalPages}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
              {hasMore && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="ml-auto sm:ml-0"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Carregando...
                    </>
                  ) : (
                    "Carregar mais"
                  )}
                </Button>
              )}
            </div>
          )}
          </>
          )}
        </CardContent>
      </Card>

      <ProductDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        product={selectedProduct}
        workspaceId={workspaceId}
      />

      <WebScrapeImporter
        open={isScrapeDialogOpen}
        onOpenChange={setIsScrapeDialogOpen}
        workspaceId={workspaceId || ""}
        onProductImported={refreshProductsAndStats}
      />

      <CsvImportDialog
        open={isCsvImportOpen}
        onOpenChange={setIsCsvImportOpen}
        workspaceId={workspaceId || ""}
        onImportComplete={refreshProductsAndStats}
      />

      {/* Confirmação de exclusão */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDialog({ open: false, count: 0, isAllSystem: false });
            setConfirmTypedText("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Excluir {confirmDialog.count} {confirmDialog.count === 1 ? "produto" : "produtos"}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      Esta ação é <strong>permanente</strong> e não pode ser desfeita. Os produtos serão removidos do catálogo, do RAG e de todos os agentes.
                    </span>
                  </div>
                </div>
                {confirmDialog.isAllSystem && (
                  <p className="text-sm text-muted-foreground">
                    A exclusão será feita em lotes de 300 produtos para não sobrecarregar o sistema. Você pode acompanhar e interromper o processo.
                  </p>
                )}
                {confirmDialog.count > 100 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">
                      Para confirmar, digite <span className="font-mono font-bold">APAGAR</span> abaixo:
                    </p>
                    <Input
                      autoFocus
                      value={confirmTypedText}
                      onChange={(e) => setConfirmTypedText(e.target.value)}
                      placeholder="APAGAR"
                      className="font-mono"
                    />
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={confirmDialog.count > 100 && confirmTypedText !== "APAGAR"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de progresso */}
      <Dialog open={bulkDeleteProgress !== null}>
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Excluindo produtos...</DialogTitle>
            <DialogDescription>
              {bulkDeleteProgress
                ? `${bulkDeleteProgress.current.toLocaleString("pt-BR")} de ${bulkDeleteProgress.total.toLocaleString("pt-BR")} produto(s)`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Progress
              value={
                bulkDeleteProgress && bulkDeleteProgress.total > 0
                  ? (bulkDeleteProgress.current / bulkDeleteProgress.total) * 100
                  : 0
              }
            />
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  cancelDeleteRef.current = true;
                }}
              >
                <X className="w-4 h-4 mr-1.5" />
                Parar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
